import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
    // Periodic token validity check (every 5 minutes)
    const interval = setInterval(() => {
      if (localStorage.getItem('auth_token')) {
        checkAuth();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const userData = await authAPI.getMe();
        setUser({
          ...userData,
          avatar_url: resolveAvatarUrl(userData.avatar_url),
          email_verified: userData.email_verified || false
        });
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('auth_token');
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    localStorage.setItem('auth_token', response.token);
    setUser({
      id: response.id,
      name: response.name,
      email: response.email,
      role: response.role || 'umum',
      email_verified: response.email_verified || false
    });
    setIsAuthenticated(true);
    return response;
  };

  const register = async (name, email, password, role = 'umum') => {
    const response = await authAPI.register({ name, email, password, role });
    localStorage.setItem('auth_token', response.token);
    setUser({
      id: response.id,
      name: response.name,
      email: response.email,
      role: response.role || 'umum',
      email_verified: response.email_verified || false
    });
    setIsAuthenticated(true);
    return response;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (updates) => {
    if (updates.avatar_url) {
      updates = { ...updates, avatar_url: resolveAvatarUrl(updates.avatar_url) };
    }
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    checkAuth,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
